import { EditorConfig } from "@ckeditor/ckeditor5-core";
import { ClassicEditor } from "@ckeditor/ckeditor5-editor-classic";
import {
  Autoformat,
  AutoLink,
  Autosave,
  Bold,
  Essentials,
  Highlight,
  ImageToolbar,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  Strikethrough,
  TodoList,
  Underline,
  WordCount,
  Undo,
  // AutoImage, // Available but not used yet
  // ImageInsert, // Available but not used yet
} from "./ckeditor-plugins";
import extraPlugins from "./plugins";

export const LICENSE_KEY = "GPL";

// Base plugins - always included
export const BASE_EDITOR_PLUGINS = [
  Autoformat,
  AutoLink,
  Autosave,
  Bold,
  Essentials,
  Highlight,
  ImageToolbar,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  Strikethrough,
  TodoList,
  Underline,
  WordCount,
  Undo,
];

// Function to create plugin array with optional extra plugins
export const createEditorPlugins = (includeExtraPlugins: boolean = false) => {
  return includeExtraPlugins 
    ? [...BASE_EDITOR_PLUGINS, ...extraPlugins]
    : BASE_EDITOR_PLUGINS;
};

// Default plugins configuration (with extra plugins enabled by default for backward compatibility)
export const CLASSIC_EDITOR_PLUGINS = createEditorPlugins(true);

// Define default configuration as a separate object
export const CLASSIC_EDITOR_CONFIG: EditorConfig = {
  // Hide the toolbar by providing empty items array
  toolbar: {
    items: [], // Empty toolbar - effectively hidden
    shouldNotGroupWhenFull: false,
  },

  // Image configuration
  image: {
    toolbar: [],
  },

  // License key
  licenseKey: LICENSE_KEY,

  // Link configuration
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://",
    decorators: {
      toggleDownloadable: {
        mode: "manual",
        label: "Downloadable",
        attributes: {
          download: "file",
        },
      },
    },
  },

  // Mention configuration
  mention: {
    feeds: [
      {
        marker: "@",
        feed: [
          /* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
        ],
      },
    ],
  },

  // Placeholder text
  placeholder: "Type or paste your content here!",

  // List configuration
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
};

// Create a custom editor class WITHOUT modifying the original ClassicEditor
export default class CustomClassicEditor extends ClassicEditor {
  // Define builtin plugins for this specific editor instance (widened type to allow dynamic reassignment)
  public static override builtinPlugins: any[] = [...BASE_EDITOR_PLUGINS];

  // Define default config for this specific editor instance
  public static override defaultConfig = CLASSIC_EDITOR_CONFIG;

  // FIXED: Add a static method to create editor with proper plugin handling
  public static override async create(
    sourceElementOrData: HTMLElement | string,
    config?: EditorConfig & { includeExtraPlugins?: boolean }
  ): Promise<CustomClassicEditor> {
    // Extract the extra plugins flag and remove it from config to avoid passing it to CKEditor
    const { includeExtraPlugins = true, ...cleanConfig } = config || {};
    
    // Create plugins array based on the flag
    const pluginsToUse = createEditorPlugins(includeExtraPlugins);
    
    // Handle plugin conflicts: if user provides plugins, use them instead of builtinPlugins
    const hasCustomPlugins = cleanConfig?.plugins && cleanConfig.plugins.length > 0;

    let mergedConfig: EditorConfig;

    if (hasCustomPlugins) {
      // User provided custom plugins - use them and merge with default config
      mergedConfig = {
        ...CLASSIC_EDITOR_CONFIG,
        ...cleanConfig,
        // Keep user's plugins as-is
        plugins: cleanConfig.plugins,
      };

      // Temporarily override builtinPlugins to avoid conflicts
      const originalBuiltinPlugins = this.builtinPlugins;
      this.builtinPlugins = [];

      try {
        const editor = (await super.create(
          sourceElementOrData,
          mergedConfig
        )) as CustomClassicEditor;
        // Restore original builtinPlugins
        this.builtinPlugins = originalBuiltinPlugins;
        return editor;
      } catch (error) {
        // Restore original builtinPlugins even if creation fails
        this.builtinPlugins = originalBuiltinPlugins;
        throw error;
      }
    } else {
      // No custom plugins provided - use builtinPlugins with optional extra plugins
      // Temporarily set builtinPlugins based on the flag
      const originalBuiltinPlugins = this.builtinPlugins;
      this.builtinPlugins = pluginsToUse;
      
      mergedConfig = {
        ...CLASSIC_EDITOR_CONFIG,
        ...cleanConfig,
        // Remove any plugins from config to avoid conflicts with builtinPlugins
      };

      // Ensure no plugins in config when using builtinPlugins
      delete mergedConfig.plugins;

      try {
        const editor = (await super.create(
          sourceElementOrData,
          mergedConfig
        )) as CustomClassicEditor;
        // Restore original builtinPlugins
        this.builtinPlugins = originalBuiltinPlugins;
        return editor;
      } catch (error) {
        // Restore original builtinPlugins even if creation fails
        this.builtinPlugins = originalBuiltinPlugins;
        throw error;
      }
    }
  }
}

// Export both the class and the configuration separately for flexibility
export {
  CustomClassicEditor,
  CLASSIC_EDITOR_CONFIG as ClassicEditorConfig,
  CLASSIC_EDITOR_PLUGINS as ClassicEditorPlugins,
};

// Also export a convenience function to create the editor
export const createClassicEditor = async (
  element: HTMLElement | string,
  customConfig?: Partial<EditorConfig> & { includeExtraPlugins?: boolean }
): Promise<CustomClassicEditor> => {
  return CustomClassicEditor.create(element, customConfig);
};
